import {
  notificationsSlice,
  usePostApiV1NotificationsMarkAllMutation,
} from '@wellesley/notifications'
import { AppsLink } from '@wellesley/router'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tabs,
  TabsControls,
  Button,
} from '@wellesley/ui'
import { Loader } from '@wellesley/apps'
import { BellRing, SettingsIcon, X } from 'lucide-react'
import { FC, PropsWithChildren } from 'react'
import { useSelector } from 'react-redux'

import useVisibleNotifications from '../hooks/useVisibleNotifications'
import NotificationsList from '../NotificationsList'
import { useSwitchState } from 'hooks/useSwitchState'
import { getNotificationsRoute } from 'helpers/routes'

const topPanelHeight = 65
const bottomPanelHeight = 72
const verticalSpacing = 32
const innerPadding = 16

type CmpProps = PropsWithChildren & {
  isLoading: boolean
}

const NotificationsPopover: FC<CmpProps> = ({ children, isLoading }) => {
  const [isOpen, , close, toggle] = useSwitchState(false)

  const allNotifications = useSelector(
    notificationsSlice.selectors.selectNotifications,
  )

  const unreadNotifications = useSelector(
    notificationsSlice.selectors.selectUnreadNotifications,
  )

  const [markAll] = usePostApiV1NotificationsMarkAllMutation()

  const { containerRef: allContainerRef, visibleCount: allVisibleCount } =
    useVisibleNotifications(allNotifications, isOpen)

  const { containerRef: unreadContainerRef, visibleCount: unreadVisibleCount } =
    useVisibleNotifications(unreadNotifications, isOpen)

  const listMaxHeight = `calc(100vh - ${topPanelHeight}px - ${bottomPanelHeight}px - ${verticalSpacing}px - ${innerPadding}px)`

  return (
    <Popover modal={false} open={isOpen} onOpenChange={toggle}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        align="center"
        side="right"
        sideOffset={20}
        alignOffset={0}
        className="z-[61] my-s w-[400px] bg-white !p-0"
        style={{
          height: `calc(100vh - ${verticalSpacing}px)`,
        }}
      >
        <Tabs defaultValue="all">
          <div className="flex items-center justify-between px-m pt-m">
            <h3 className="mb-xxxs text-lg font-semibold text-gray-900 ">
              Notifications
            </h3>
            <X size={24} className="cursor-pointer" onClick={close} />
          </div>
          <div className="mb-[1px] flex items-center justify-between border-b border-gray-200 px-m">
            <TabsControls
              className="!m-0 w-[88px] !p-0 after:hidden"
              items={[
                { value: 'all', label: 'All' },
                { value: 'unread', label: 'Unread' },
              ]}
            />
            <span
              className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900"
              onClick={() => void markAll({ status: 'READ' })}
            >
              Mark all as read
            </span>
          </div>
          {isLoading ? (
            <Loader className="mx-auto my-l" />
          ) : (
            <>
              <NotificationsList
                notifications={allNotifications}
                containerRef={allContainerRef}
                visibleCount={allVisibleCount}
                listMaxHeight={listMaxHeight}
                tabValue="all"
              />
              <NotificationsList
                notifications={unreadNotifications}
                containerRef={unreadContainerRef}
                visibleCount={unreadVisibleCount}
                listMaxHeight={listMaxHeight}
                tabValue="unread"
              />
            </>
          )}
        </Tabs>
        <div className="absolute bottom-s left-[1px] right-[1px] flex items-center gap-xs rounded-b-lg bg-white p-s">
          <AppsLink to={getNotificationsRoute()} className="w-full">
            <Button variant="secondary" className="w-full">
              <BellRing size={20} /> To notifications page
            </Button>
          </AppsLink>
          <AppsLink to="/settings/:tab?" props={{ tab: 'notifications' }}>
            <Button variant="secondary" size="icon_m" className="rounded-m">
              <SettingsIcon size={20} />
            </Button>
          </AppsLink>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default NotificationsPopover
